import { supportedLanguages as langMap } from "../../../assets/i18h/appTranslation";
import phoneService from "../../../services/phoneService";
import { PROFESSION_TITLES } from "./registrationWizardConstants";

const SUPPORTED_LANGUAGES = Object.values(langMap);

const normalizeLanguageCode = (value) => {
  if (!value) return "";
  const normalized = String(value).toLowerCase().replace(/_/g, "-");
  const primary = normalized.split("-")[0];
  if (SUPPORTED_LANGUAGES.includes(normalized)) return normalized;
  if (SUPPORTED_LANGUAGES.includes(primary)) return primary;
  return "";
};

export const resolveLanguage = (value) => normalizeLanguageCode(value) || langMap.en;

export const hasExplicitLanguage = (value) => Boolean(normalizeLanguageCode(value));

/** Country → supported language. Unlisted countries fall back to English. */
const COUNTRY_TO_LANGUAGE = {
  // English
  US: langMap.en,
  GB: langMap.en,
  CA: langMap.en,
  AU: langMap.en,
  IE: langMap.en,
  NZ: langMap.en,
  // Spanish
  ES: langMap.es,
  MX: langMap.es,
  AR: langMap.es,
  CO: langMap.es,
  CL: langMap.es,
  PE: langMap.es,
  VE: langMap.es,
  EC: langMap.es,
  BO: langMap.es,
  PY: langMap.es,
  UY: langMap.es,
  CR: langMap.es,
  PA: langMap.es,
  GT: langMap.es,
  HN: langMap.es,
  SV: langMap.es,
  NI: langMap.es,
  CU: langMap.es,
  DO: langMap.es,
  PR: langMap.es,
  // Arabic
  SA: langMap.ar,
  AE: langMap.ar,
  EG: langMap.ar,
  QA: langMap.ar,
  MA: langMap.ar,
  IQ: langMap.ar,
  KW: langMap.ar,
  // Russian
  RU: langMap.ru,
  BY: langMap.ru,
  KZ: langMap.ru,
  KG: langMap.ru,
  // Portuguese
  BR: langMap.pt,
  PT: langMap.pt,
  AO: langMap.pt,
  MZ: langMap.pt,
  // Hebrew
  IL: langMap.he,
};

export const resolveLanguageFromGeo = (geoInfo = {}) => {
  const countryCode = String(geoInfo.country_code || geoInfo.country || "").toUpperCase();
  return COUNTRY_TO_LANGUAGE[countryCode] || langMap.en;
};

export const normalizeProfessionTitle = (title) => {
  if (!title) return "";
  const raw = String(title).trim();
  const match = PROFESSION_TITLES.find(
    (item) =>
      item.value.toUpperCase() === raw.toUpperCase() ||
      item.label.toUpperCase() === raw.toUpperCase()
  );
  return match?.value || "";
};

export const parseBirthDateParts = (birthDate) => {
  if (!birthDate) {
    return { birthYear: "", birthMonth: "", birthDay: "" };
  }
  const parts = String(birthDate).split("-");
  if (parts.length !== 3) {
    return { birthYear: "", birthMonth: "", birthDay: "" };
  }
  return {
    birthYear: parts[0],
    birthMonth: String(Number(parts[1])),
    birthDay: String(Number(parts[2])),
  };
};

/**
 * Apply TherapistForRegistrationDto mapped account onto wizard step fields.
 */
export const applyApiAccountToStepData = (stepData, account = {}) => {
  if (!stepData || !account) return stepData;

  stepData.firstName.value = account.firstName || "";
  stepData.lastName.value = account.lastName || "";

  if (account.profession) {
    stepData.profession.value = normalizeProfessionTitle(account.profession);
  }

  if (hasExplicitLanguage(account.languageForPatients)) {
    stepData.languageForPatients.value = resolveLanguage(account.languageForPatients);
  }
  if (hasExplicitLanguage(account.clinicianLanguage)) {
    stepData.clinicianLanguage.value = resolveLanguage(account.clinicianLanguage);
  } else if (hasExplicitLanguage(account.languageForPatients)) {
    stepData.clinicianLanguage.value = resolveLanguage(account.languageForPatients);
  }

  if (account.phone) {
    stepData.phone.value = phoneService.normalizeSubAccountPhone(account.phone);
  }

  if (account.profilePhotoUrl) {
    stepData.photo.value = {
      dataUrl: account.profilePhotoUrl,
      existing: true,
    };
  }

  if (account.gender) {
    stepData.gender.value = String(account.gender).toUpperCase();
  }

  const dob = parseBirthDateParts(account.birthDate);
  stepData.birthYear.value = dob.birthYear;
  stepData.birthMonth.value = dob.birthMonth;
  stepData.birthDay.value = dob.birthDay;

  if (account.termsAndConditionsAccepted) {
    stepData.acceptTerms.value = true;
  }

  return stepData;
};
